/**
 * Seeded randomness. The sim never touches Math.random: every draw comes from a named stream whose
 * 32-bit state lives inside the game state, so saves, the server replay and bots all reproduce exactly.
 * Separate streams keep systems independent: adding draws to "market" never shifts "playtest".
 */
import { dcos, dlog } from "./dmath";

/** 32-bit FNV-1a hash of a string. */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** One mulberry32 step: returns [nextState, value in [0, 1)]. */
export function mulberry32(state: number): [number, number] {
  const s = (state + 0x6d2b79f5) | 0;
  let t = Math.imul(s ^ (s >>> 15), 1 | s);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return [s >>> 0, ((t ^ (t >>> 14)) >>> 0) / 4294967296];
}

export type RngStore = Record<string, number>;

export class Rng {
  constructor(
    private readonly store: RngStore,
    private readonly key: string,
  ) {
    if (store[key] === undefined) store[key] = hashString(`${store.__seed ?? 0}:${key}`);
  }

  next(): number {
    const [s, v] = mulberry32(this.store[this.key]);
    this.store[this.key] = s;
    return v;
  }

  float(min = 0, max = 1): number {
    return min + (max - min) * this.next();
  }

  /** Integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    return min + Math.floor(this.next() * (max - min + 1));
  }

  chance(p: number): boolean {
    return this.next() < p;
  }

  pick<T>(list: readonly T[]): T {
    return list[Math.floor(this.next() * list.length)];
  }

  /** Weighted pick; weights may be zero. Falls back to the first item if all weights are zero. */
  weighted<T>(list: readonly T[], weight: (item: T) => number): T {
    let total = 0;
    for (const x of list) total += Math.max(0, weight(x));
    if (total <= 0) return list[0];
    let r = this.next() * total;
    for (const x of list) {
      r -= Math.max(0, weight(x));
      if (r < 0) return x;
    }
    return list[list.length - 1];
  }

  /** Normal distribution (Box–Muller). */
  normal(mean = 0, sd = 1): number {
    const u = Math.max(1e-9, this.next());
    const v = this.next();
    return mean + sd * Math.sqrt(-2 * dlog(u)) * dcos(2 * Math.PI * v);
  }

  shuffle<T>(list: T[]): T[] {
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  }
}

/** A throwaway stream from a fixed key, for pure functions that must not touch the game's streams. */
export function fixedRng(key: string): Rng {
  const store: RngStore = { __seed: hashString(key) };
  return new Rng(store, "fixed");
}
